import { z } from "zod";

export const assessmentCategories = ["STANDARD", "FEMALE_ENTERTAINMENT", "FEMALE_TECHNICAL"] as const;
export type AssessmentCategory = (typeof assessmentCategories)[number];
export const assessmentCategoryLabels: Record<AssessmentCategory, string> = { STANDARD: "段位考核", FEMALE_ENTERTAINMENT: "女娱乐", FEMALE_TECHNICAL: "女技术" };

export const examModes = ["SINGLE", "DOUBLE"] as const;
export const ranks = ["NEW", "ELITE", "ACE", "PEAK"] as const;
export const applicationStatuses = [
  "SUBMITTED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
  "REJECTED",
  "IDENTITY_INVALID"
] as const;
export const groupResults = ["PASS", "FAIL"] as const;
export const participantTypes = ["CANDIDATE", "TEMPORARY_PARTNER"] as const;
export const staffRoles = ["EXAMINER", "ADMIN"] as const;
export const liveStatuses = ["ACTIVE", "COMPLETED", "CANCELLED"] as const;
export const qualificationSources = ["FREE", "PAID"] as const;
export const paidEntitlementStatuses = ["AVAILABLE", "RESERVED", "CONSUMED", "INVALID"] as const;

export type ExamMode = (typeof examModes)[number];
export type Rank = (typeof ranks)[number];
export type ApplicationStatus = (typeof applicationStatuses)[number];
export type GroupResult = (typeof groupResults)[number];
export type ParticipantType = (typeof participantTypes)[number];
export type StaffRole = (typeof staffRoles)[number];
export type LiveStatus = (typeof liveStatuses)[number];
export type QualificationSource = (typeof qualificationSources)[number];
export type PaidEntitlementStatus = (typeof paidEntitlementStatuses)[number];

export const rankLabels: Record<Rank, string> = {
  NEW: "新锐",
  ELITE: "精英",
  ACE: "王牌",
  PEAK: "巅峰"
};

export const modeLabels: Record<ExamMode, string> = {
  SINGLE: "单考",
  DOUBLE: "双考"
};

export const applicationStatusLabels: Record<ApplicationStatus, string> = {
  SUBMITTED: "已提交",
  IN_PROGRESS: "考核中",
  COMPLETED: "已完成",
  CANCELLED: "已取消",
  REJECTED: "已拒绝",
  IDENTITY_INVALID: "身份无效"
};

export const applicationInputSchema = z
  .object({
    mode: z.enum(examModes),
    assessmentCategory: z.enum(assessmentCategories).default("STANDARD"),
    rank: z.enum(ranks).nullable().optional().default(null),
    examinerId: z.string().uuid(),
    partnerWechatId: z.string().trim().min(4).max(64).optional()
  })
  .superRefine((value, context) => {
    if (value.assessmentCategory === "STANDARD" && !value.rank) context.addIssue({ code: "custom", message: "段位考核必须选择段位", path: ["rank"] });
    if (value.assessmentCategory !== "STANDARD" && value.rank !== null) context.addIssue({ code: "custom", message: "女陪考核不使用段位", path: ["rank"] });
    if (value.assessmentCategory !== "STANDARD" && value.mode === "SINGLE" && value.partnerWechatId) context.addIssue({ code: "custom", message: "单考不能填写搭档微信号", path: ["partnerWechatId"] });
    if (value.assessmentCategory === "FEMALE_ENTERTAINMENT" && value.mode !== "SINGLE") context.addIssue({ code: "custom", message: "娱乐考核仅支持单考", path: ["mode"] });
    if (value.assessmentCategory === "STANDARD" && value.mode === "SINGLE" && value.rank === "NEW") {
      context.addIssue({ code: "custom", message: "单考没有新锐段位", path: ["rank"] });
    }
    if (value.mode === "DOUBLE" && !value.partnerWechatId) {
      context.addIssue({ code: "custom", message: "双考必须填写搭档微信号", path: ["partnerWechatId"] });
    }
  });

export const candidateProfileSchema = z.object({
  wechatId: z.string().trim().min(4).max(64),
  warningConfirmed: z.literal(true)
});

export const staffLoginSchema = z.object({
  username: z.string().trim().min(3).max(40),
  password: z.string().min(8).max(128)
});

function makePasswordChangeSchema(newPassword: z.ZodString, minimumLength: number) {
  return z
  .object({
    currentPassword: z.string().min(8).max(128),
    newPassword,
    confirmPassword: z.string().min(minimumLength).max(128)
  })
  .superRefine((value, context) => {
    if (value.newPassword !== value.confirmPassword) {
      context.addIssue({ code: "custom", message: "两次输入的新密码不一致", path: ["confirmPassword"] });
    }
    if (value.currentPassword === value.newPassword) {
      context.addIssue({ code: "custom", message: "新密码不能与临时密码相同", path: ["newPassword"] });
    }
  });
}

export const examinerPasswordChangeSchema = makePasswordChangeSchema(
  z.string()
    .min(8, "新密码至少 8 位")
    .max(128)
    .regex(/[a-zA-Z]/, "新密码必须包含字母")
    .regex(/[0-9]/, "新密码必须包含数字"),
  8
);

export const adminPasswordChangeSchema = makePasswordChangeSchema(
  z.string()
    .min(12, "新密码至少 12 位")
    .max(128)
    .regex(/[a-z]/, "新密码必须包含小写字母")
    .regex(/[A-Z]/, "新密码必须包含大写字母")
    .regex(/[0-9]/, "新密码必须包含数字"),
  12
);

export const startExamSchema = z.object({
  participantsVerified: z.literal(true)
});

export const roundStatInputSchema = z.object({
  participantId: z.string().uuid(),
  roundNumber: z.number().int().min(1).max(9),
  kills: z.number().int().min(0).max(999)
});

export const assessmentDetailsSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("FEMALE_ENTERTAINMENT"), rounds: z.array(z.object({ mode: z.enum(["SECRET", "TOP_SECRET"]), durationSeconds: z.number().int().min(0) }).strict()).length(2), mapKnowledge: z.boolean(), interaction: z.boolean(), basicKnowledge: z.boolean() }).strict(),
  z.object({ kind: z.literal("FEMALE_TECHNICAL"), rounds: z.array(z.object({ roundNumber: z.union([z.literal(1), z.literal(2)]), extractedValue: z.number().int().min(0) }).strict()).min(1).max(2), bonusKills: z.number().int().min(0).max(999), bonusReason: z.string().trim().max(2000) }).strict()
]);
export type AssessmentDetails = z.infer<typeof assessmentDetailsSchema>;

export const finishExamSchema = z.object({
  result: z.enum(groupResults),
  totalExtractedValue: z.number().int().min(0).nullable(),
  assessmentDetails: assessmentDetailsSchema.nullable().optional(),
  roundStats: z.array(roundStatInputSchema).max(18),
  evidenceFileIds: z.array(z.string().uuid()).min(1).max(9),
  notes: z.string().trim().max(2000).default("")
});

export function normalizeLiveUrlInput(value: string) {
  const trimmed = value.trim();
  if (!trimmed) throw new Error("请输入直播链接或抖音分享文案");

  try {
    const directUrl = new URL(trimmed);
    if (directUrl.protocol !== "https:") throw new Error("直播链接必须使用 HTTPS");
    if (directUrl.username || directUrl.password) throw new Error("直播链接不能包含用户名或密码");
    return directUrl.toString();
  } catch (error) {
    if (error instanceof Error && ["直播链接必须使用 HTTPS", "直播链接不能包含用户名或密码"].includes(error.message)) throw error;
  }

  const rawLinks = trimmed
    .replace(/\]\(/g, " ")
    .match(/https:\/\/[^\s<>"']+/giu) || [];
  const normalizedLinks = new Set<string>();

  for (const rawLink of rawLinks) {
    const candidate = rawLink.replace(/[\[\](){}，。！？；：、,.!?;:]+$/gu, "");
    let parsed: URL;
    try {
      parsed = new URL(candidate);
    } catch {
      throw new Error("抖音分享文案中的直播链接格式无效");
    }
    if (parsed.protocol !== "https:" || parsed.hostname !== "v.douyin.com") {
      throw new Error("分享文案仅支持 v.douyin.com 的 HTTPS 链接");
    }
    if (parsed.username || parsed.password) throw new Error("分享链接不能包含用户名或密码");
    normalizedLinks.add(parsed.toString());
  }

  if (normalizedLinks.size === 1) return [...normalizedLinks][0];
  if (normalizedLinks.size > 1) throw new Error("分享文案中存在多个不同的直播链接");
  throw new Error("未在分享文案中找到有效的抖音直播链接");
}

export const liveStartSchema = z.object({
  liveUrl: z.string().max(4000, "直播链接或分享文案不能超过 4000 个字符").transform((value, context) => {
    try {
      return normalizeLiveUrlInput(value);
    } catch (error) {
      context.addIssue({ code: "custom", message: error instanceof Error ? error.message : "直播链接格式无效" });
      return z.NEVER;
    }
  })
});

export const liveEndSchema = z.object({
  proofFileIds: z.array(z.string().uuid()).min(1).max(3)
});

export const liveCancelSchema = z.object({}).strict();

export const resultCorrectionSchema = z.object({
  result: z.enum(groupResults),
  totalExtractedValue: z.number().int().min(0).nullable(),
  assessmentDetails: assessmentDetailsSchema.nullable().optional(),
  roundStats: z.array(roundStatInputSchema).max(18),
  evidenceFileIds: z.array(z.string().uuid()).min(1).max(9),
  notes: z.string().trim().max(2000).default(""),
  reason: z.string().trim().min(4).max(1000)
});

export const staffCreateSchema = z.object({
  username: z.string().trim().regex(/^[a-zA-Z0-9_.-]{3,40}$/),
  displayName: z.string().trim().min(2).max(30),
  password: z.string().min(8).max(128).regex(/[a-zA-Z]/, "临时密码必须包含字母").regex(/[0-9]/, "临时密码必须包含数字"),
  bio: z.string().trim().max(200).default(""),
  livePlatform: z.string().trim().max(40).default("")
});

export const weeklyAttemptGrantSchema = z.object({
  reason: z.string().trim().min(4, "增加机会的原因至少 4 个字符").max(200)
});

export const paidAssessmentOrderSchema = z.object({
  orderNumber: z.string().trim().min(4, "请输入完整订单号").max(64).regex(/^[A-Za-z0-9_-]+$/, "订单号格式不正确")
});

export const paidAssessmentCandidateParamsSchema = z.object({
  candidateId: z.string().uuid("考生标识无效")
});

export const assessmentOrderValidationSchema = z.object({
  eligible: z.boolean(),
  reasonCode: z.string().min(1),
  orderNumber: z.string().min(1).nullable(),
  skuId: z.number().int().nullable(),
  assessmentMode: z.enum(examModes).nullable(),
  orderStatus: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  paidAmount: z.string().nullable(),
  paidAt: z.string().nullable(),
  customerNameMasked: z.string().nullable(),
  customerWechatMasked: z.string().nullable()
}).strict();

export type AssessmentOrderValidation = z.infer<typeof assessmentOrderValidationSchema>;

export type PaidAssessmentSummary = {
  single: number;
  double: number;
  entitlements: Array<{
    id: string;
    orderNumberMasked: string;
    mode: ExamMode;
    status: PaidEntitlementStatus;
    redeemedAt: string;
  }>;
};

export const assessedCandidatePageSchema = z.object({
  q: z.string().trim().min(1, "请输入微信昵称或微信号").max(80),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursorCompletedAt: z.string().datetime({ offset: true }).optional(),
  cursorCandidateId: z.string().uuid().optional()
}).superRefine((value, context) => {
  if (Boolean(value.cursorCompletedAt) !== Boolean(value.cursorCandidateId)) {
    context.addIssue({ code: "custom", message: "分页游标不完整" });
  }
});

const historyCursorFields = {
  limit: z.coerce.number().int().min(1).max(50).default(20),
  cursorAt: z.string().datetime({ offset: true }).optional(),
  cursorId: z.string().uuid().optional()
};

const strictDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "日期格式无效").refine((value) => {
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day;
}, "日期无效");

export const assessmentHistoryQuerySchema = z.object({
  ...historyCursorFields,
  date: strictDateString.optional(),
  assessmentCategory: z.enum(assessmentCategories).optional()
}).superRefine((value, context) => {
  if (Boolean(value.cursorAt) !== Boolean(value.cursorId)) context.addIssue({ code: "custom", message: "分页游标不完整" });
});

export const liveHistoryQuerySchema = z.object({
  ...historyCursorFields,
  date: strictDateString.optional()
}).superRefine((value, context) => {
  if (Boolean(value.cursorAt) !== Boolean(value.cursorId)) context.addIssue({ code: "custom", message: "分页游标不完整" });
});

export const adminAssessmentHistoryQuerySchema = assessmentHistoryQuerySchema.safeExtend({
  candidateId: z.string().uuid("考生标识无效")
});

export const adminLiveHistoryQuerySchema = liveHistoryQuerySchema.safeExtend({
  examinerId: z.string().uuid("考官标识无效")
});

export const assessmentCalendarQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "月份格式无效")
});

export const examinerWeeklyAttemptGrantSchema = weeklyAttemptGrantSchema.extend({
  requestId: z.string().uuid()
});

export const assessedCandidateGrantParamsSchema = z.object({
  candidateId: z.string().uuid("考生标识无效")
});

export const examinerAvatarSchema = z.object({
  fileId: z.string().uuid()
});

export type WeeklyOpportunity = {
  weekStart: string;
  base: number;
  bonus: number;
  used: number;
  total: number;
  remaining: number;
};

export type HistoryEvidence = { id: string; name: string; url: string };

export type AssessmentHistoryVersion = {
  id: string;
  version: number;
  isCurrent: boolean;
  groupResult: GroupResult;
  totalExtractedValue: number | null;
  assessmentDetails?: AssessmentDetails | null;
  notes: string;
  correctionReason?: string | null;
  createdAt: string;
  createdBy: string;
  roundStats: Array<{ participantId: string; roundNumber: number; kills: number }>;
  evidence: HistoryEvidence[];
};

export type AssessmentHistoryItem = {
  applicationId: string;
  candidateId: string;
  candidateName: string;
  examinerId: string;
  examinerName: string;
  mode: ExamMode;
  assessmentCategory: AssessmentCategory;
  rank: Rank | null;
  startedAt?: string | null;
  completedAt: string;
  durationSeconds: number;
  currentVersion: number;
  participants: Array<{ id: string; role: string; participantType: string; wechatId: string }>;
  versions: AssessmentHistoryVersion[];
};

export type LiveHistoryItem = {
  id: string;
  examinerId: string;
  examinerName: string;
  liveUrl: string;
  status: LiveStatus;
  startedAt: string;
  endedAt?: string | null;
  durationSeconds: number;
  countsTowardPerformance: boolean;
  assessmentCount: number;
  passedAssessmentCount: number;
  attributionComplete: boolean;
  evidence: HistoryEvidence[];
};

export type HistoryPage<T> = {
  items: T[];
  pagination: { limit: number; hasMore: boolean; nextCursor: { at: string; id: string } | null };
};

export type AssessmentCalendarDay = {
  date: string;
  assessmentCount: number;
  passedAssessmentCount: number;
  liveSessionCount: number;
  effectiveLiveDurationSeconds: number;
  cancelledLiveCount: number;
  activeLiveCount: number;
};

export type ApiSession =
  | { kind: "ANONYMOUS"; csrfToken: string }
  | {
      kind: "CANDIDATE";
      csrfToken: string;
      candidateId: string;
      displayName: string;
      profileComplete: boolean;
    }
  | {
      kind: "STAFF";
      csrfToken: string;
      staffId: string;
      role: StaffRole;
      displayName: string;
      mustChangePassword: boolean;
    };
